/// <amd-module name="Work/Views/MyWork/View/MyWorkList" />
import * as React from "react";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { Components, VssComponent, IVssComponentContext, css } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { getAssetLocation } from "VSS/Platform/Location";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { equals, format } from "VSS/Core/Util/String";

import { ConstrainMode, DetailsListLayoutMode, SelectionMode, IColumn } from "OfficeFabric/DetailsList";
import { IGroupDividerProps, IGroup } from "OfficeFabric/GroupedList";

import { VssDetailsList, VssDetailsListRowStyle, VssDetailsListGroupHeader, VssDetailsListTitleCell } from "VSSUI/VssDetailsList";
import { Button } from "VSSUI/Button";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Link } from "VSSUI/Link";
import { Tooltip } from "VSSUI/TooltipEx";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

import { ITfsPageService } from "Tfs/Platform/Page";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";

import * as MyWorkResources from "Work/Views/MyWork/Resources";
import { MyWorkListComponentProps } from "Work/Views/MyWork/View/MyWorkList.Props";
import { MyWorkSection } from "Work/Views/MyWork/Contracts/MyWorkSection";
import { MyWorkItemsDecorated } from "Work/Views/MyWork/Contracts/MyWorkItemsDecorated";
import { UsageTelemetryHelper } from "Work/Views/MyWork/Telemetry";

import { IMyWorkProvider, ProviderState, IMyWorkProviderStateChangeArgs } from "DataProviders/IMyWorkProvider";
import { MyWorkProvider } from "DataProviders/MyWorkProvider";
import { MyWorkAssignedToMeDataProvider } from "../DataProviders/MyWorkAssignedToMeDataProvider";
import { MyWorkMentionsProvider } from "DataProviders/MyWorkMentionsProvider";
import { MyWorkMyActivityProvider } from "DataProviders/MyWorkMyActivityProvider";
import { activityDateFieldName } from "DataProviders/MyWorkProviderBase";

import { QueryOption, WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { WorkItemTypeIconProvider, WorkItemTrackingDataProvider, WorkItemStateProvider } from "Wit/UI/Data";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { WorkItemState } from "Wit/UI/WorkItemState";
import { IVssPageContext } from 'VSS/Platform/Context';

const enum PivotIdentifier {
    assignedToMe = "AssignedToMe",
    following = "FollowedByMe",
    mentioned = "Mentioned",
    myActivity = "MyActivity"
}

export interface MyWorkListComponentState {
    sections:  { [key: string]: MyWorkSection };
}

/**
 * New view component for the work items 'Me' page. Includes sections for Assigned to me, Mentioned, Following, and My Activity.
 */
export class MyWorkListComponent extends VssComponent<MyWorkListComponentProps, MyWorkListComponentState> {
    private static readonly AREA = "wit";
    private static readonly FLYOUT_PERF_SCENARIO_NAME = "MyWorkFlyout-Initialize";
    private static readonly PAGE_PERF_SCENARIO_NAME = "MyWorkPage-Initialize";
    
    private workItemListProviders: { [key: string]: IMyWorkProvider } = {};
    private _performanceScenarioName: string;
    private _isPageInteractive: boolean;
    private _tableColumns: IColumn[];
    private _workItemTrackingDataProvider: WorkItemTrackingDataProvider;
    private _projectName: string;
    private _performanceEnded: boolean = false;
    private _groups: { [key: string]: IGroup };
    private _assignedToMeGroup: IGroup;
    private _myWorkAssignedToMeDataProvider: MyWorkAssignedToMeDataProvider;
    private _pivotItemLengths: { [key: string]: number } = {
        [PivotIdentifier.assignedToMe]: 0,
        [PivotIdentifier.mentioned]: 0,
        [PivotIdentifier.following]: 0,
        [PivotIdentifier.myActivity]: 0
    };
    private _pivotToNameMap: { [key: string]: string } = {
        [PivotIdentifier.assignedToMe]: MyWorkResources.AssignedToMe,
        [PivotIdentifier.mentioned]: MyWorkResources.Mentioned,
        [PivotIdentifier.following]: MyWorkResources.Following,
        [PivotIdentifier.myActivity]: MyWorkResources.MyActivity
    }

    constructor(props: MyWorkListComponentProps, context: IVssComponentContext) {
        super(props, context);

        this._performanceScenarioName = !!this.props.extendedDisplay
            ? MyWorkListComponent.PAGE_PERF_SCENARIO_NAME
            : MyWorkListComponent.FLYOUT_PERF_SCENARIO_NAME;
        // if extendedDisplay - should log perf scenario as TTI.
        this._isPageInteractive = !!this.props.extendedDisplay;

        const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        // Only start perf measurement for FPS switch and the flyout
        if (perfService.isFastPageSwitchScenario() || !this._isPageInteractive) {
            perfService.startScenario(this._performanceScenarioName, this._isPageInteractive);
        }

        this._workItemTrackingDataProvider = new WorkItemTrackingDataProvider(this.context.pageContext);

        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            this._projectName = tfsPageData.project.name;
        } else {
            this._projectName = "";
        }

        // Set up group and section for Assigned to me
        this._myWorkAssignedToMeDataProvider = new MyWorkAssignedToMeDataProvider(this.context.pageContext);
        let decoratedItems: MyWorkItemsDecorated[] = [];
        for (var item of this._myWorkAssignedToMeDataProvider.getWorkItems()) {
            decoratedItems.push({
                ...item, 
                sectionKey: PivotIdentifier.assignedToMe,
                sectionName: MyWorkResources.AssignedToMe
            });
        }

        this._pivotItemLengths[PivotIdentifier.assignedToMe] = decoratedItems.length;
        const section: MyWorkSection = {
            sectionKey: PivotIdentifier.assignedToMe,
            sectionName: MyWorkResources.AssignedToMe, 
            items: decoratedItems,
            isCollapsed: false,
            hasMore: false,
            count: this._myWorkAssignedToMeDataProvider.getWorkItemCount()
        };
        
        this.state = {
            sections: {
                [PivotIdentifier.assignedToMe]: section
            }
        };

        this._assignedToMeGroup = {
            key: section.sectionKey,
            name: section.sectionName,
            startIndex: this._getStartIndex(PivotIdentifier.assignedToMe), 
            count: section.count,
            isCollapsed: section.isCollapsed,
            hasMoreData: section.hasMore,
            data: section
        }
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._initializeProviders();
    }

    public render(): JSX.Element {
        const { className, isCollapsible } = this.props;

        if (!this._tableColumns) {
            this._buildColumns();
        }

        if (!this._groups) {
            this._buildGroups();
        }

        const isAssignedToMeZeroData: boolean = this._myWorkAssignedToMeDataProvider.getWorkItemCount() === 0;

        const providerState = this._getProviderState();

        // if extended display is false (rendering the flyout) and assigned to me has zero data, show zero data experience. 
        // if extended display is true and everything has zero data, show the zero data experience for the assigned to me section only 
        if (isAssignedToMeZeroData && (!this.props.extendedDisplay || providerState === ProviderState.LoadedZeroData)) {
            return this.getZeroDataContent(
                "ms.vss-work-web/my-work-content/Content/zerodata-your-work-in-one-place.svg",
                MyWorkResources.ZeroData_AssignedToMe_Primary,
                MyWorkResources.ZeroData_AssignedToMe_Secondary,
                MyWorkResources.ZeroData_LinkText,
                MyWorkResources.ZeroData_AssignedToMe_Link
            );
        } else if (isAssignedToMeZeroData && (providerState === ProviderState.Loading || providerState === ProviderState.Uninitialized)) {
            // If assigned to me is empty, then wait for all other dropdowns to load before displaying to prevent other tabs from jumping/changing positions while loading. 
            return this.getSpinner();
        } else if (providerState === ProviderState.Error) {
            return <div />;
        }

        // Flatten Assigned to me group and groups dictionary into list 
        const groups: IGroup[] = [];
        let totalCurrentSize = this._assignedToMeGroup.count;
        if (this._assignedToMeGroup.count !== 0) {
            
            groups.push(this._assignedToMeGroup);
            this._pivotItemLengths[PivotIdentifier.assignedToMe] = this._assignedToMeGroup.count;
        }
        for (var key in this._groups) {
            if (this._groups[key].count !== 0) {
                this._pivotItemLengths[key] = totalCurrentSize;
                this._groups[key].startIndex = totalCurrentSize;
                groups.push(this._groups[key])
                totalCurrentSize += this._groups[key].count;
            }
        }

        return (
            <div className={css("mywork-panel flex-column flex-grow", className)}>
                <div className="work-items-table-label body-m secondary-text">
                    <VssDetailsList
                        className="my-work-list"
                        items={this._getItems()}
                        groups={groups}
                        columns={this._tableColumns}
                        isHeaderVisible={false}
                        rowStyle={VssDetailsListRowStyle.twoLine}
                        constrainMode={ConstrainMode.unconstrained}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        hideGroupExpansion={true}
                        groupProps={{
                            onRenderHeader: isCollapsible ? this._onRenderCollapsibleHeader : this._onRenderHeader,
                            headerProps: {
                                selectionMode: SelectionMode.none,
                                isSelected: false,
                                selected: false
                            },
                            onRenderFooter: this._onRenderFooter
                        }}
                        onRowDidMount={() => {
                            if (!this._performanceEnded) {
                                if (this._myWorkAssignedToMeDataProvider.getWorkItemCount() !== 0) { 
                                    // If Assigned to me is loaded and the row is rendered, then end scenario
                                    this._endPerformanceScenario(ProviderState.Loaded);
                                }
                            }
                        }}
                    />
                </div>
            </div>
        );
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        for (const key in this.workItemListProviders) {
            if (this.workItemListProviders.hasOwnProperty(key)) {
                const provider = this.workItemListProviders[key];

                if (provider) {
                    provider.unsubscribe(this.onProviderStateChanged);
                }
            }
        }
    }

    /**
     * Returns the provider state of all providers except Assigned to me, which is loaded initially. 
     * Only returns Loaded or LoadedZeroData states when all providers are Loaded or LoadedZeroData.
     */
    private _getProviderState(): ProviderState {
        let providerState = ProviderState.Uninitialized;
        let loadedCount = 0;
        let zeroDataCount = 0;
        const totalProvidersCount =  Object.keys(this.workItemListProviders).length;

        if (totalProvidersCount === 0) {
            return providerState;
        }

        for (var key of Object.keys(this.workItemListProviders)) {
            switch(this.workItemListProviders[key].getState()) {
                case ProviderState.Error:
                case ProviderState.Uninitialized:
                case ProviderState.Loading:
                    return this.workItemListProviders[key].getState();
                case ProviderState.Loaded:
                    loadedCount++;
                    break;
                case ProviderState.LoadedZeroData: 
                    zeroDataCount++;
                    break;
            }
        }

        // Return ProviderState.LoadedZeroData if all providers are zero data to show the zero data experience page. 
        if (zeroDataCount === totalProvidersCount) {
            return ProviderState.LoadedZeroData;
        }

        // Return ProviderState.Loaded if all providers are loaded or zero data. 
        if ((loadedCount === totalProvidersCount) || (loadedCount + zeroDataCount === totalProvidersCount)) {
            return ProviderState.Loaded;
        }

        return providerState
    }

    private _createSectionAndGroup(pivot: PivotIdentifier): void {
        const name = this._pivotToNameMap[pivot];
        let items: WorkItem[] = [];
        let section: MyWorkSection = {
            sectionKey: pivot,
            sectionName: name, 
            items: [],
            isCollapsed: true,
            hasMore: false,
            count: 0
        }

        const provider = this.workItemListProviders[pivot] ? this.workItemListProviders[pivot] : this.getProvider(this.context, pivot);
        items = provider.getWorkItems();
        section.count = provider.getWorkItemsCount();
        
        let decoratedItems: MyWorkItemsDecorated[] = [];
        for (var item of items) {
            decoratedItems.push({
                ...item, 
                sectionKey: pivot,
                sectionName: name
            });
        }

        this._pivotItemLengths[pivot] = decoratedItems.length;
        section.items = decoratedItems;

        const currSections = this.state.sections;
        currSections[pivot] = section;
        this.setState({ sections: currSections });

        this._groups[pivot] = {
            key: section.sectionKey,
            name: section.sectionName,
            startIndex: this._getStartIndex(pivot), 
            count: section.count,
            isCollapsed: section.isCollapsed,
            hasMoreData: section.hasMore,
            data: section
        }    
    }

    /**
     * Used to get the start index of a group for the VssDetailsList
     * @param groupKey pivot key
     */
    private _getStartIndex(groupKey: PivotIdentifier): number {
        let startIndex = 0;

        switch (groupKey) {
            case PivotIdentifier.assignedToMe: 
                startIndex = 0;
                break;
            case PivotIdentifier.mentioned:
                startIndex = this._pivotItemLengths[PivotIdentifier.assignedToMe];
                break;
            case PivotIdentifier.following:
                startIndex = this._pivotItemLengths[PivotIdentifier.assignedToMe] + this._pivotItemLengths[PivotIdentifier.mentioned];
                break;
            case PivotIdentifier.myActivity:
                startIndex = this._pivotItemLengths[PivotIdentifier.assignedToMe] + this._pivotItemLengths[PivotIdentifier.mentioned] + this._pivotItemLengths[PivotIdentifier.following];
                break;
        }

        return startIndex;
    }

    /**
     * Combines the section items into a single list for the VssDetailsList to use. 
     */
    private _getItems = (): MyWorkItemsDecorated[] => {
        let items: MyWorkItemsDecorated[] = [];

        if (PivotIdentifier.assignedToMe in this.state.sections) {
            items = items.concat(this.state.sections[PivotIdentifier.assignedToMe].items);
        }
        if (PivotIdentifier.mentioned in this.state.sections) {
            items = items.concat(this.state.sections[PivotIdentifier.mentioned].items);
        }
        if (PivotIdentifier.following in this.state.sections) {
            items = items.concat(this.state.sections[PivotIdentifier.following].items);
        }
        if (PivotIdentifier.myActivity in this.state.sections) {
            items = items.concat(this.state.sections[PivotIdentifier.myActivity].items);
        }

        return items;
    }

    /**
     * Begins fetching data for Following, Mentioned, and My activity.
     * Assigned to me is loaded separately to get the data before TTI.
     * Only create Following, Mentioned, and My activity providers if not using the flyout.
     */
    private _initializeProviders(): void {
        if (this.props.extendedDisplay) {
            this.getProvider(this.context, PivotIdentifier.following).initialize();
            this.getProvider(this.context, PivotIdentifier.mentioned).initialize();
            this.getProvider(this.context, PivotIdentifier.myActivity).initialize();
        }
    }

    private _onRenderHeader = (groupProps: IGroupDividerProps): JSX.Element => {
        return <VssDetailsListGroupHeader text={groupProps.group!.name} />;
    };

    private _onRenderCollapsibleHeader = (groupProps: IGroupDividerProps): JSX.Element => {
        return <CollapsibleGroupHeader {...groupProps} pageContext={this.context.pageContext}  />;
    };


    /**
     * If the group is empty, show zero data experience. 
     * If the group has exceeded its limit, show limit message
     */
    private _onRenderFooter = (groupProps: IGroupDividerProps): JSX.Element | null => {
        if (!groupProps.group) {
            return <div></div>;
        }

        const groupKey = groupProps.group.key as PivotIdentifier;
        let limitJsx: JSX.Element | null = null;
        if (groupProps.group.key == PivotIdentifier.assignedToMe && !groupProps.group.isCollapsed) {
            if (this._myWorkAssignedToMeDataProvider.getLimitExceeded()) {
                limitJsx = <div className="limit-message">{this._getLimitMessage(groupKey)}</div>;
            }
        }

        return limitJsx;
    }

    private _getLimitMessage(key: PivotIdentifier): string {
        if (key === PivotIdentifier.assignedToMe) {
            return MyWorkResources.AssignedToMeQueryLimitExceeded;
        }

        return "";
    }

    private getZeroDataContent(
        resourceName: string,
        primaryText: string,
        secondaryText: string | JSX.Element,
        linkText: string,
        link: string
    ): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, resourceName);
        return (
            <div className="flex flex-column flex-grow">
                <ZeroData
                    imagePath={image}
                    imageAltText={primaryText}
                    primaryText={primaryText}
                    secondaryText={secondaryText}
                    actionText={linkText}
                    actionType={ZeroDataActionType.link}
                    actionHref={link}
                />
            </div>
        );
    }

    private getSpinner(): JSX.Element {
        return (
            <div className="flex flex-column flex-grow mywork-panel">
                <Spinner className="my-work-items-spinner" size={SpinnerSize.large} label={MyWorkResources.Loading} />
            </div>
        );
    }

    private getProvider = (context: IVssComponentContext, pivotKey: PivotIdentifier, queryOption?: QueryOption): IMyWorkProvider => {
        if (this.workItemListProviders[pivotKey]) {
            return this.workItemListProviders[pivotKey];
        }

        let result: IMyWorkProvider;

        switch (pivotKey) {
            case PivotIdentifier.following:
                result = new MyWorkProvider(context.pageContext, QueryOption.Followed);
                break;
            case PivotIdentifier.myActivity:
                result = new MyWorkMyActivityProvider(context.pageContext);
                break;
            case PivotIdentifier.mentioned:
                result = new MyWorkMentionsProvider(context.pageContext);
                break;
            default:
                throw new Error(`Unknown data provider: ${pivotKey}`);
        }

        this.workItemListProviders[pivotKey] = result;

        result.subscribe((providerStateChange, action) => this.onProviderStateChanged(providerStateChange, pivotKey, action));

        return result;
    };

    private onProviderStateChanged = (providerStateChange: IMyWorkProviderStateChangeArgs, key: PivotIdentifier, action?: string): void => {
        const { previousState, state } = providerStateChange;

        // When the provider state changes update the UI
        this.forceUpdate(() => {
            if (previousState === ProviderState.Loading) {
                this._createSectionAndGroup(key);

                // Only end scenario if either assigned to me is NOT empty or unitialized and is loaded, or if assigned to me is empty and all other pivots are loaded or if everything is 0 data. 
                // Should only end performance once.
                if (!this._performanceEnded) {
                    if (this._myWorkAssignedToMeDataProvider.getWorkItemCount() === 0 && (this._getProviderState() === ProviderState.Loaded || this._getProviderState() === ProviderState.LoadedZeroData)) {
                        this._endPerformanceScenario(state);
                    }
                }
            }
        });

        if (state === ProviderState.Error) {
            const bannerService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
            bannerService.addBanner({ level: 2, dismissable: true, message: MyWorkResources.General_Error }, true);
        }
    };

    private _endPerformanceScenario = (state: ProviderState): void => {
        const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        this._performanceEnded = true;
        const startTime = Date.now();
        // end performance scenario in next tick as DetailsList gets rendered asynchronously
        // once we replace it with table component we can remove setTimeout workaround
        setTimeout(() => {
            const elapsedTime = Date.now() - startTime;
            // Record perf telemetry about the time it took to load/draw the UI
            perfService.endScenario(MyWorkListComponent.AREA, this._performanceScenarioName, this._isPageInteractive, undefined, {
                state: ProviderState[state],
                gridRenderTime: elapsedTime
            });
        }, 0);
    }

    private _buildGroups() {
        this._groups = {
            [PivotIdentifier.mentioned]: {
                key: PivotIdentifier.mentioned,
                name: MyWorkResources.Mentioned,
                startIndex: 0,
                count: 0,
                isCollapsed: true,
                hasMoreData: false,
                data: []
            },
            [PivotIdentifier.following]: {
                key: PivotIdentifier.following,
                name: MyWorkResources.Following,
                startIndex: 0,
                count: 0,
                isCollapsed: true,
                hasMoreData: false,
                data: []
            },
            [PivotIdentifier.myActivity]: {
                key: PivotIdentifier.myActivity,
                name: MyWorkResources.MyActivity,
                startIndex: 0,
                count: 0,
                isCollapsed: true,
                hasMoreData: false,
                data: []
            }
        };
    }
    
    private _buildColumns() {
        this._tableColumns = [
            {
                key: "icon",
                name: "",
                fieldName: "",
                minWidth: 15,
                maxWidth: 15,
                onRender: (item: WorkItem, index: number, column: IColumn) => {
                    return (
                        <WorkItemTypeIconProvider
                            dataProvider={this._workItemTrackingDataProvider}
                            dataSource={{
                                projectIdOrName: item.fields[CoreFieldRefNames.TeamProject],
                                workItemTypeName: item.fields[CoreFieldRefNames.WorkItemType]
                            }}
                        >
                            {icon => <WorkItemTypeIcon className={"my-work-list-column"} data={icon} />}
                        </WorkItemTypeIconProvider>
                    );
                }
            } as IColumn,
            {
                key: "title",
                name: "",
                fieldName: "",
                minWidth: 250, // Need to specify this to ensure that we span the available horizontal space
                onRender: (item: WorkItem, index: number, column: IColumn) => {
                    const secondaryRowFormat = MyWorkResources.WorkItemsSecondaryLine;
                    const secondaryItems: JSX.Element[] = [];
                    secondaryItems.push(<span key="id">{item.id}</span>);
                    secondaryItems.push(
                        <Link href={item._links.project} key="project" subtle={true}>
                            {item.fields[CoreFieldRefNames.TeamProject]}
                        </Link>
                    );
                    return (
                        <VssDetailsListTitleCell
                            key={"title" + index}
                            onRenderPrimaryText={() => (
                                <FPSLink
                                className="work-item-title body-l text-ellipsis"
                                href={item._links.html}
                                subtle={true}
                                supportsFPS={equals(this._projectName, item.fields[CoreFieldRefNames.TeamProject], true)}
                                telemetrySource="wit-my-work-view"
                                telemetryProperties={{ clickTarget: "work-item-title" }}
                                removeUnderline={true}
                                >
                                    <Tooltip overflowOnly={true} text={item.fields[CoreFieldRefNames.Title]}>
                                        <div className="text-ellipsis">{item.fields[CoreFieldRefNames.Title]}</div>
                                    </Tooltip>
                                </FPSLink>
                            )}
                            onRenderSecondaryText={() => (
                                <FormatComponent className="secondary-text body-s" format={secondaryRowFormat}>
                                    {secondaryItems}
                                </FormatComponent>
                            )}
                        />
                    );
                }
            } as IColumn,
            {
                key: "state",
                name: "", 
                fieldName: "",
                minWidth: 90,
                onRender: (item: WorkItem, index: number, column: IColumn) => {
                    return (
                        <WorkItemStateProvider
                            dataProvider={this._workItemTrackingDataProvider}
                            dataSource={{
                                projectIdOrName: item.fields[CoreFieldRefNames.TeamProject],
                                workItemTypeName: item.fields[CoreFieldRefNames.WorkItemType],
                                stateName: item.fields[CoreFieldRefNames.State]
                            }}
                        >
                            {stateAndColor => <WorkItemState className={"my-work-list-column"} data={stateAndColor} hideText={false} />}
                        </WorkItemStateProvider>
                    );
                }
            } as IColumn
        ];

        // The flyout should not show the timestamp
        if (this.props.extendedDisplay) {
            this._tableColumns.push({
                key: "LastUpdated",
                name: "", 
                fieldName: "",
                minWidth: 200,
                onRender: (item: WorkItem, index: number, column: IColumn) => {
                    return (
                        <Tooltip overflowOnly={true} text={item.fields[activityDateFieldName].date.toLocaleString(undefined, undefined)}>
                            <div className="flex flex-grow text-ellipsis my-work-list-column">{item.fields[activityDateFieldName].formattedDate}</div>
                        </Tooltip>
                    );
                }
            } as IColumn);
        }
    }
}

interface CollapsibleGroupHeaderState {
    isCollapsed: boolean;
}

interface CollapsibleGroupHeaderProps extends IGroupDividerProps {
    pageContext: IVssPageContext;
}

class CollapsibleGroupHeader extends React.PureComponent<CollapsibleGroupHeaderProps, CollapsibleGroupHeaderState> {
    constructor(props: CollapsibleGroupHeaderProps) {
        super(props);
        this.state = { isCollapsed: props.group!.isCollapsed || false };
    }

    public render(): JSX.Element {
        const group = this.props.group!;

        const iconName = this.state.isCollapsed ? "ChevronDown" : "ChevronUp";
        const toggleButtonAriaLabel = this.state.isCollapsed
            ? format(MyWorkResources.ExpandAriaLabel, group.name)
            : format(MyWorkResources.CollapseAriaLabel, group.name);

        const groupTitle = group.name;

        return (
            <div className="collapsible-group-header" data-is-focusable="true">
                <Button
                    subtle={true}
                    iconProps={{ iconName }}
                    onClick={this._onToggle}
                    ariaExpanded={!this.state.isCollapsed}
                    ariaLabel={toggleButtonAriaLabel}
                />
                <span className={"group-header-title"}>{groupTitle}</span>
            </div>
        );
    }

    private _onToggle = (): void => {
        const { onToggleCollapse, group, pageContext } = this.props;
        if (onToggleCollapse) {
            onToggleCollapse(group!);
        }

        // record usage telemetry
        UsageTelemetryHelper.publishMyWorkDropdownClickActivity(pageContext, group!.key, this.state.isCollapsed);

        this.setState({ isCollapsed: !this.state.isCollapsed });
    };
}

Components.add("my-work-list", MyWorkListComponent);
